/**
 * Permission keys, read rather than written.
 *
 * A key is the server's word for a role. Client code used to spell one itself,
 * from the shared builder and a build-time copy of the deployment name, in about
 * fifteen screens. It no longer can: the grammar lives on the server alone
 * (`apps/server/src/lib/authz/keys.ts`,
 * docs/adr/34-every-key-names-the-owning-organisation.md, "One key builder. The
 * ELASO Core server is the only builder of keys. Clients receive keys and never
 * construct one"), and every key a client needs now arrives on data it already
 * reads: each roster role carries `key`, each workspace carries `everyoneKey`.
 *
 * So the two directions a screen used to get by string surgery are lookups here:
 *
 *   ref  → key    the roster row's `key`, or nothing when the roster has no such
 *                 role. Only `named` refs resolve. A `channel` ref is a
 *                 per-channel pseudo-role with no roster row and no key to send
 *                 back, and every caller here already discarded it.
 *   key  → what   a roster role, this workspace's "everyone", or something to
 *                 skip: a channel pseudo-role, a role from another workspace, a
 *                 role deleted since the key was stored.
 *
 * Nothing here parses a key. `classify` matches whole keys against the roster's
 * own and the workspace's own, so a grammar change on the server is invisible
 * here (TECH-3336).
 */
import type { ElasoRoleRef } from "@elaso/shared/types/authorization";
import { useMemo } from "react";
import { useStore } from "zustand";
import { coreStore, type Role } from "@/lib/stores/core-store";

/** What a permission key turns out to name, once looked up. */
export type ClassifiedRoleKey =
	| { kind: "role"; role: Role }
	| { kind: "everyone" }
	/** Not a role this workspace's roster knows: skip it, do not display it. */
	| { kind: "skip" };

/** The roster indexed by permission key, the inverse of its id index. */
export function indexRosterByKey(roster: Map<string, Role>): Map<string, Role> {
	const byKey = new Map<string, Role>();
	for (const role of roster.values()) {
		if (role.key) byKey.set(role.key, role);
	}
	return byKey;
}

/**
 * The key for one role ref, or `null` when the roster cannot name it: a
 * `channel` pseudo-role, a role from another workspace, or one already deleted.
 */
export function roleKeyForRef(
	ref: ElasoRoleRef,
	roster: Map<string, Role>
): string | null {
	if (ref.kind !== "named") return null;
	return roster.get(ref.id)?.key ?? null;
}

/** The keys for a list of refs, with the ones the roster cannot name dropped. */
export function roleKeysForRefs(
	refs: readonly ElasoRoleRef[],
	roster: Map<string, Role>
): string[] {
	const keys: string[] = [];
	for (const ref of refs) {
		const key = roleKeyForRef(ref, roster);
		if (key) keys.push(key);
	}
	return keys;
}

export function classifyRoleKey(
	key: string,
	byKey: Map<string, Role>,
	everyoneKey: string | undefined
): ClassifiedRoleKey {
	if (everyoneKey && key === everyoneKey) return { kind: "everyone" };
	const role = byKey.get(key);
	return role ? { kind: "role", role } : { kind: "skip" };
}

export type RoleKeyLookup = {
	/** The workspace's roles by id, as the store holds them. */
	roster: Map<string, Role>;
	/**
	 * The key standing for "everyone" in this workspace, or `undefined` before
	 * `workspace.list` has landed. A boot from the client-side cache holds a
	 * partial workspace row until then, and a screen offering an "everyone" row
	 * simply does not offer it yet.
	 */
	everyoneKey: string | undefined;
	keyForRef: (ref: ElasoRoleRef) => string | null;
	keysForRefs: (refs: readonly ElasoRoleRef[]) => string[];
	classify: (key: string) => ClassifiedRoleKey;
};

/**
 * The lookups above, bound to one workspace's roster and everyone key and
 * memoised on them.
 */
export function useRoleKeys(
	workspaceId: string | null | undefined
): RoleKeyLookup {
	const roster = useStore(coreStore, (state) => state.roles);
	const everyoneKey = useStore(
		coreStore,
		(state) =>
			(workspaceId ? state.workspaces.get(workspaceId) : undefined)?.everyoneKey
	);

	return useMemo(() => {
		const byKey = indexRosterByKey(roster);
		return {
			roster,
			everyoneKey,
			keyForRef: (ref) => roleKeyForRef(ref, roster),
			keysForRefs: (refs) => roleKeysForRefs(refs, roster),
			classify: (key) => classifyRoleKey(key, byKey, everyoneKey),
		};
	}, [roster, everyoneKey]);
}
